// The case team, out of whatever shape the endpoint sent.
//
// `GET /ot/cases/<id>/team/` returns an OBJECT:
//
//     { team: [...], missing_mandatory_roles: [...] }
//
// Two panels read it and both assumed a bare array. `(team || []).filter(...)`
// on an object throws "team.filter is not a function" — and the two failures
// looked nothing alike:
//
//   PreopPanel  threw during render. React has no error boundary by default,
//               so the whole tab unmounted and the browser showed a white
//               page. It rendered correctly while the state was still null
//               and blanked the instant the fetch resolved, which is why it
//               read as "empty, then nothing".
//
//   TheatrePanel threw inside a .then(), where its own .catch swallowed it and
//               set the list to empty. No error anywhere; the order
//               authorisation picker simply never offered a doctor.
//
// One silent, one fatal, one cause. Hence a named function with a test rather
// than the same defensive expression written out twice — the next caller gets
// it right without knowing the story.

/**
 * The team members from a team payload.
 *
 * Accepts the object the endpoint really returns, a bare array (what callers
 * wrongly expected, and what a future endpoint might send), or nothing at all.
 * Always returns an array, so callers can filter and map without guarding.
 *
 * @param {{team?: unknown[]}|unknown[]|null|undefined} payload
 * @returns {unknown[]}
 */
export function teamMembers(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.team)) return payload.team;
  return [];
}

/**
 * Roles the case still has nobody in, as the server reported them.
 *
 * Served alongside the members and previously thrown away — the tab strip was
 * recomputing "is this role filled" from the member list instead, which cannot
 * know about a mandatory role that has no member to be derived from.
 *
 * @returns {string[]}
 */
export function missingMandatoryRoles(payload) {
  if (!payload || Array.isArray(payload)) return [];
  return Array.isArray(payload.missing_mandatory_roles)
    ? payload.missing_mandatory_roles
    : [];
}
